/**
 * Mapping dos códigos cnetmobile (gov.br) para labels human-readable.
 *
 * Fonte: response de `/comprasnet-fase-externa/v1/enums` capturado via mitm
 * 08/05/2026 (snapshot em backend/docs/cnetmobile-enums.json).
 */

export type BadgeColor =
  | 'orange'
  | 'emerald'
  | 'red'
  | 'yellow'
  | 'slate'
  | 'blue'
  | 'purple';

export const BADGE_COLOR_CLASSES: Record<BadgeColor, string> = {
  orange: 'bg-orange-500/20 text-orange-400',
  emerald: 'bg-emerald-500/20 text-emerald-400',
  red: 'bg-red-500/20 text-red-400',
  yellow: 'bg-yellow-500/20 text-yellow-400',
  slate: 'bg-slate-500/20 text-slate-400',
  blue: 'bg-blue-500/20 text-blue-400',
  purple: 'bg-purple-500/20 text-purple-400',
};

/** SituacaoPropostaItemEnum — status da proposta IPCOM no item */
export const SITUACAO_PROPOSTA_LABELS: Record<string, string> = {
  '1': 'Desclassificada',
  '2': 'Desclassificada (julgamento)',
  '3': 'Aceita',
  '4': 'Habilitado',
  '5': 'Inabilitado',
  '6': 'Adjudicada',
  '7': 'Pendente cota',
  '8': 'Recusou cota',
  '9': 'Desclassificada (disputa)',
};

/** FaseItemEnum — fase atual do item */
export const FASE_ITEM_LABELS: Record<string, string> = {
  AS: 'Aguardando abertura',
  AP: 'Análise propostas',
  F: 'Aguardando disputa',
  AA: 'Aguardando lances',
  LS: 'Abertura suspensa',
  LA: 'Etapa aberta',
  LF: 'Etapa fechada',
  AL: 'Etapa aberta (aleatório)',
  FE: 'Aguardando encerramento',
  RA: 'Aguardando reinício',
  AF: 'Aguardando disputa fechada',
  AM: 'Aguardando desempate ME/EPP',
  DM: 'Em desempate ME/EPP',
  A7: 'Aguardando desempate 7174',
  D7: 'Em desempate 7174',
  DF: 'Em disputa final',
  B3: 'Aguardando decisão cota',
  JT: 'Aguardando julgamento técnica',
  E: 'Aguardando julgamento',
  JE: 'Julgamento encerrado',
  HE: 'Habilitação encerrada',
  PE: 'Aguardando encerramento',
  PR: 'Aguardando reabertura',
  JR: 'Julgamento reaberto',
  AR: 'Aberto recursos',
  AC: 'Aberto contrarrazões',
  D1: 'Aguardando decisão recursos',
  D2: 'Aguardando revisão decisão',
  AE: 'Apto homologação',
};

/** SituacaoItemCompraEnum — situação geral do item */
export const SITUACAO_ITEM_LABELS: Record<string, string> = {
  '1': 'Ativo',
  '2': 'Cancelado',
  '3': 'Anulado',
  '13': 'Anulado',
  '4': 'Revogado',
  '14': 'Revogado',
  '5': 'Suspenso',
  '6': 'Deserto',
  '7': 'Fracassado (análise)',
  '8': 'Fracassado (julgamento)',
  '9': 'Fracassado (disputa)',
};

/** Tipo da row de procurement_compra_items vinda do backend */
export interface CompraItem {
  id?: number;
  numero: number;
  descricao?: string;
  itemFase?: string;
  itemSituacao?: string;
  homologado?: boolean;
  julgHabEncerrada?: boolean;
  propostaClassificacao?: number;
  propostaSituacao?: string;
  valorEstimadoUnitario?: number;
  quantidadeSolicitada?: number;
}

/**
 * Escolhe o badge mais informativo pra exibir.
 * Prioridade:
 *   1. Status terminal da licitação (revogado/anulado/cancelado/fracassado)
 *   2. Status da proposta IPCOM (adjudicada/habilitado/inabilitado/etc)
 *   3. Fase do item (aguardando_julgamento/apto_homologacao/etc)
 *   4. Fallback: "Em andamento"
 */
export function getCompraItemBadge(item: CompraItem): {
  label: string;
  color: BadgeColor;
} {
  // 1. Terminais negativos da licitação
  const terminalSituacao: Record<string, { label: string; color: BadgeColor }> = {
    '2': { label: 'Cancelado', color: 'red' },
    '3': { label: 'Anulado', color: 'red' },
    '13': { label: 'Anulado', color: 'red' },
    '4': { label: 'Revogado', color: 'red' },
    '14': { label: 'Revogado', color: 'red' },
    '5': { label: 'Suspenso', color: 'yellow' },
    '6': { label: 'Deserto', color: 'slate' },
    '7': { label: 'Fracassado', color: 'red' },
    '8': { label: 'Fracassado', color: 'red' },
    '9': { label: 'Fracassado', color: 'red' },
  };
  if (item.itemSituacao && terminalSituacao[item.itemSituacao]) {
    return terminalSituacao[item.itemSituacao];
  }

  // 2. Status da proposta IPCOM (mais relevante pro user)
  if (item.propostaSituacao === '6') return { label: 'Adjudicada', color: 'orange' };
  if (item.propostaSituacao === '4') return { label: 'Habilitado', color: 'emerald' };
  if (item.propostaSituacao === '3') return { label: 'Aceita', color: 'emerald' };
  if (item.propostaSituacao === '5') return { label: 'Inabilitado', color: 'red' };
  if (['1', '2', '9'].includes(item.propostaSituacao || '')) {
    return { label: 'Desclassificada', color: 'red' };
  }
  if (item.propostaSituacao === '7') return { label: 'Pendente cota', color: 'yellow' };
  if (item.propostaSituacao === '8') return { label: 'Recusou cota', color: 'slate' };

  // 3. Homologado flag (boolean — quando todos sub-itens convergem)
  if (item.homologado) return { label: 'Homologado', color: 'orange' };

  // 4. Fase do item
  if (item.itemFase === 'E') return { label: 'Aguardando julgamento', color: 'orange' };
  if (item.itemFase === 'AE') return { label: 'Apto homologação', color: 'orange' };
  if (item.itemFase === 'JE') return { label: 'Julgamento encerrado', color: 'blue' };
  if (item.itemFase === 'HE') return { label: 'Habilitação encerrada', color: 'blue' };
  if (item.itemFase === 'AR') return { label: 'Aberto recursos', color: 'yellow' };
  if (item.itemFase === 'AC') return { label: 'Aberto contrarrazões', color: 'yellow' };
  if (['LA', 'LF', 'AL'].includes(item.itemFase || '')) {
    return { label: 'Em disputa', color: 'blue' };
  }

  // Fallback: usa label da fase se conhecida
  if (item.itemFase && FASE_ITEM_LABELS[item.itemFase]) {
    return { label: FASE_ITEM_LABELS[item.itemFase], color: 'slate' };
  }

  return { label: 'Em andamento', color: 'slate' };
}

// ── URLs públicas de portal (Encontrar / Disputar / Monitorar) ─────────────
// Montadas a partir do `pncpId` canônico / `linkOrigem` cru. O `linkOrigem`
// costuma apontar pra páginas que exigem login ("acesso não autorizado"); estas
// helpers preferem a página PÚBLICA correspondente, que abre sem autenticação.

/** URL pública do edital no PNCP a partir do pncpId canônico `{ano}-{seq}-{cnpj}`.
 *  Retorna null pros itens AL-* sem chave PNCP. */
export function pncpPublicUrl(pncpId?: string | null): string | null {
  const m = String(pncpId || '').match(/^(\d{4})-(\d+)-(\d{14})$/);
  if (!m) return null;
  const [, ano, seq, cnpj] = m;
  return `https://pncp.gov.br/app/editais/${cnpj}/${ano}/${seq}`;
}

// Página PÚBLICA DIRETA de acompanhamento (sem login). A landing
// `/public/landing?destino=acompanhamento-compra` é um redirect do SPA que
// trava em "Aguarde…"/"não autorizado" na maioria das vezes (depende de sessão/
// bootstrap). A rota direta `/public/compras/acompanhamento-compra?compra=` é a
// mesma que o backend usa no comprasnet-public-fetch e abre sempre. (29/06/2026)
const CNET_PUBLIC_BASE =
  'https://cnetmobile.estaleiro.serpro.gov.br/comprasnet-web/public/compras/acompanhamento-compra?compra=';

/** Código de modalidade legado do SIASG/ComprasNet (2 dígitos) a partir do nome.
 *  NÃO é o `modalidadeId` do PNCP. Confirmado por engenharia reversa do param
 *  `compra` de itens reais: Pregão=05, Concorrência=03, Dispensa=06. Modalidades
 *  sem código confirmado (Inexigibilidade, Chamamento, Leilão…) → null. */
function cnetModalidadeCode(modalidadeNome?: string | null): string | null {
  const n = String(modalidadeNome || '').toLowerCase();
  if (!n) return null;
  if (n.includes('pregão') || n.includes('pregao')) return '05';
  if (n.includes('dispensa')) return '06';
  if (n.includes('concorrência') || n.includes('concorrencia')) return '03';
  return null;
}

/** URL PÚBLICA de acompanhamento no ComprasNet (Compras.gov.br) — a landing
 *  `/public/landing?destino=acompanhamento-compra` abre SEM login. O param
 *  `compra` (17 dígitos) é `pad(uasg,6) + modCnet(2) + pad(numero,5) + ano(4)`,
 *  onde `modCnet` é o código legado do SIASG (≠ `modalidadeId` do PNCP).
 *
 *  Itens com `portalOrigem='comprasnet'` SÃO operados no Compras.gov.br mesmo
 *  quando o `linkOrigem` é PNCP (o PNCP só espelha; a página do PNCP traz "Fonte:
 *  Compras.gov.br"). Por isso preferimos sempre a landing pública do ComprasNet:
 *    1) `linkOrigem` já é a landing → usa direto;
 *    2) link antigo `ConsultaLicitacoes` (coduasg/modprp/numprp) → reconstrói;
 *    3) constrói por campos (uasg+numero+ano+modalidade) quando a modalidade tem
 *       código confirmado. Sem dados suficientes/modalidade desconhecida → null
 *       (cai no fallback PNCP público). */
export function comprasnetPublicUrl(item?: {
  portalOrigem?: string | null;
  linkOrigem?: string | null;
  codigoUnidade?: string | null;
  numeroCompra?: string | null;
  anoCompra?: number | string | null;
  modalidadeNome?: string | null;
}): string | null {
  if (!item || String(item.portalOrigem || '').toLowerCase() !== 'comprasnet') return null;
  const link = String(item.linkOrigem || '');
  // 1) Já é um link ComprasNet com o param `compra` (landing OU acompanhamento) →
  // extrai a chave e normaliza pra rota DIRETA pública (a landing trava).
  const jaTemCompra = link.match(/[?&]compra=(\d{15,})/i);
  if (/cnetmobile/i.test(link) && jaTemCompra) return CNET_PUBLIC_BASE + jaTemCompra[1];
  // 2) Link antigo (ConsultaLicitacoes: coduasg/modprp/numprp) → reconstrói.
  const old = link.match(/coduasg=(\d+)[^]*?modprp=(\d+)[^]*?numprp=(\d+)/i);
  if (old) {
    const [, uasg, mod, numprp] = old;
    return CNET_PUBLIC_BASE + uasg.padStart(6, '0') + mod.padStart(2, '0') + numprp;
  }
  // 3) Constrói por campos (cobre os itens cujo linkOrigem é PNCP).
  const uasg = String(item.codigoUnidade || '').replace(/\D/g, '');
  const numero = String(item.numeroCompra || '').replace(/\D/g, '');
  const ano = String(item.anoCompra || '').replace(/\D/g, '');
  const modCnet = cnetModalidadeCode(item.modalidadeNome);
  if (uasg && numero && ano.length === 4 && modCnet) {
    return CNET_PUBLIC_BASE + uasg.padStart(6, '0') + modCnet + numero.padStart(5, '0') + ano;
  }
  return null;
}
